import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ApiService, { initialAuthState } from "../../services/ApiService";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function AddEditPromocode() {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData || null;

  const [formData, setFormData] = useState({
    promocode: "",
    discount: 0,
    discountType: "Percent",
    minOrderAmount: null,
    maxDiscountAmount: null,
    usageLimit: null,
    userLimit: null,
    startDate: new Date(),
    endDate: new Date(),
    applicableProducts: [],
    promoUsers: {},
    companyCode: initialAuthState.companyCode,
    unitCode: initialAuthState.unitCode,
  });

  const [products, setProducts] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Load edit data if available
  useEffect(() => {
    if (editData) {
      setFormData({
        promocode: editData.promocode,
        discount: editData.discount,
        discountType: editData.discountType,
        minOrderAmount: editData.minOrderAmount,
        maxDiscountAmount: editData.maxDiscountAmount,
        usageLimit: editData.usageLimit,
        userLimit: editData.userLimit,
        startDate: new Date(editData.startDate),
        endDate: new Date(editData.endDate),
        applicableProducts: editData.applicableProducts?.map(p => ({ value: p, label: p })) || [],
        promoUsers: editData.promoUsers || {},
        companyCode: editData.companyCode,
        unitCode: editData.unitCode,
        id: editData.id,
      });
    }
  }, [editData]);

  // Fetch products for multi-select
  useEffect(() => {
    async function fetchProducts() {
      try {
        const res = await ApiService.post("/products/list", {
          companyCode: initialAuthState.companyCode,
          unitCode: initialAuthState.unitCode,
        });
        if (res.data) {
          setProducts(res.data.map(p => ({ value: p.id, label: p.name })));
        }
      } catch (err) {
        console.error("Failed to fetch products:", err);
      }
    }
    fetchProducts();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.promocode.trim()) newErrors.promocode = "Promocode required";
    if (formData.discount <= 0) newErrors.discount = "Discount must be > 0";
    if (!formData.startDate || !formData.endDate) newErrors.startDate = "Select valid dates";
    if (formData.endDate < formData.startDate) newErrors.endDate = "End date cannot be before start date";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      ...formData,
      applicableProducts: formData.applicableProducts.map(p => p.value),
      discount: parseFloat(formData.discount),
      minOrderAmount: formData.minOrderAmount ? parseFloat(formData.minOrderAmount) : null,
      maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null,
      usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
      userLimit: formData.userLimit ? parseInt(formData.userLimit) : null,
    };

    try {
      setLoading(true);
      const res = await ApiService.post("/promocode/createOrUpdate", payload);
      if (res.status) {
        alert(editData ? "Promocode updated!" : "Promocode created!");
        navigate("/promocodes");
      } else {
        alert(res.message || "Failed to save promocode");
      }
    } catch (err) {
      console.error(err);
      alert("Internal error, try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-2xl font-bold mb-6">{editData ? "Edit Promocode" : "Add Promocode"}</h2>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block mb-1">Promocode</label>
          <input
            type="text"
            name="promocode"
            value={formData.promocode}
            onChange={handleChange}
            className={`w-full p-3 border rounded-md ${errors.promocode ? "border-red-500" : "border-gray-300"}`}
          />
          {errors.promocode && <p className="text-red-500 text-sm">{errors.promocode}</p>}
        </div>

        <div className="flex space-x-4">
          <div className="flex-1">
            <label className="block mb-1">Discount</label>
            <input
              type="number"
              name="discount"
              value={formData.discount}
              onChange={handleChange}
              className={`w-full p-3 border rounded-md ${errors.discount ? "border-red-500" : "border-gray-300"}`}
            />
          </div>
          <div className="flex-1">
            <label className="block mb-1">Discount Type</label>
            <select
              name="discountType"
              value={formData.discountType}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-md"
            >
              <option value="Percent">Percent</option>
              <option value="Amount">Amount</option>
            </select>
          </div>
        </div>

        <div className="flex space-x-4">
          <div className="flex-1">
            <label className="block mb-1">Min Order Amount</label>
            <input type="number" name="minOrderAmount" value={formData.minOrderAmount || ''} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-md" placeholder="Optional" />
          </div>
          <div className="flex-1">
            <label className="block mb-1">Max Discount Amount</label>
            <input type="number" name="maxDiscountAmount" value={formData.maxDiscountAmount || ''} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-md" placeholder="Optional" />
          </div>
        </div>

        <div className="flex space-x-4">
          <div className="flex-1">
            <label className="block mb-1">Usage Limit</label>
            <input type="number" name="usageLimit" value={formData.usageLimit || ''} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-md" placeholder="Optional" />
          </div>
          <div className="flex-1">
            <label className="block mb-1">User Limit</label>
            <input type="number" name="userLimit" value={formData.userLimit || ''} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-md" placeholder="Optional" />
          </div>
        </div>

        <div className="flex space-x-4">
          <div className="flex-1">
            <label className="block mb-1">Start Date</label>
            <DatePicker
              selected={formData.startDate}
              onChange={date => setFormData(prev => ({ ...prev, startDate: date }))}
              className="w-full p-3 border border-gray-300 rounded-md"
              showYearDropdown          // show year dropdown
              dropdownMode="select"     // dropdown for year selection
              scrollableYearDropdown    // optional, makes long lists scrollable
              yearDropdownItemNumber={15} // number of years to show in dropdown
            />
          </div>
          <div className="flex-1">
            <label className="block mb-1">End Date</label>
            <DatePicker
              selected={formData.endDate}
              onChange={date => setFormData(prev => ({ ...prev, endDate: date }))}
              className="w-full p-3 border border-gray-300 rounded-md"
              showYearDropdown
              dropdownMode="select"
              scrollableYearDropdown
              yearDropdownItemNumber={15}
            />
          </div>
        </div>

        <div>
          <label className="block mb-1">Applicable Products</label>
          <Select
            isMulti
            options={products}
            value={formData.applicableProducts}
            onChange={selected => setFormData(prev => ({ ...prev, applicableProducts: selected }))}
          />
        </div>

        <button type="submit" disabled={loading} className="w-full py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700">
          {loading ? "Saving..." : editData ? "Update Promocode" : "Create Promocode"}
        </button>
      </form>
    </div>
  );
}